/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import toast from "react-hot-toast";

import { mainURL } from "constants/index";
import { urlMap } from "constants/URL";
import LOCAL_STORAGE_KEYS from "constants/STORAGE_KEYS";
import { safeParse } from "utils/safeJson";
import sleep from "utils/sleep";
import warning from "utils/warning";

import JweManager from "./JweManager";
import applySlashURL from "utils/applySlashURL";

type FetchBaseOptions = {
  shouldHandleError?: boolean;
  shouldHandleOutdated?: boolean;
  decrypt?: boolean;
  hasContentType?: boolean;
  /** API version prefix. Defaults to the v2 mainURL when omitted. */
  version?: keyof typeof urlMap;
};

type FetchInterceptionOptions = {
  slugTarget?: "url" | "body" | null;
  encrypt?: boolean;
  eventId?: string;
};

type FetchOpts = FetchInterceptionOptions & FetchBaseOptions;

export const toJSON = async (res: Response) => await res.clone().json();

const previewAble = [200, 201, 401, 422];

// A manager for sending to the server.
abstract class FetchBase {
  private static readonly URL: string = mainURL;

  private static handleOutdated = async (response: Response) => {
    if (response.status === 204 || response.ok === false) return response;

    const data = await response.clone().json();

    let appVersion: string | undefined;
    if (data.hasOwnProperty("reactapp_v")) appVersion = data.reactapp_v;

    if (appVersion) {
      const localVersion = localStorage.getItem("version");

      if (appVersion !== localVersion) {
        localStorage.setItem("version", appVersion);
        toast("New version available, the page will be refreshed...");

        setTimeout(() => {
          window.location.reload();
        }, 3000);
      }

      // to not continue with the request and wait for the page to reload.
      await sleep(5000);
    }

    return response;
  };

  private static decryption = async (response: Response) => {
    try {
      const data = await toJSON(response);

      let jwe = data;
      if (data.hasOwnProperty("payload")) jwe = data.payload;
      if (typeof jwe !== "string") throw new Error("JWE is not a string");

      const payload = await JweManager.decrypt(jwe);
      return payload;
    } catch (err) {
      warning(err);
      return response;
    }
  };

  public static fetchBase = async (
    url: string,
    requestBody: RequestInit | null = null,
    fetchOpts: FetchOpts,
  ): Promise<Response> => {
    const settings: FetchBaseOptions = {
      shouldHandleError: true,
      shouldHandleOutdated: false,
      decrypt: false,
      hasContentType: true,
    };

    if (fetchOpts) {
      Object.assign(settings, fetchOpts);
      if (fetchOpts.encrypt === true) settings.decrypt = true;
    }

    const baseURL = settings.version ? urlMap[settings.version] : this.URL;

    let Request = { headers: {} };
    if (requestBody) Object.assign(Request, requestBody);

    Request.headers = {
      // to spread headers in case if options is true, else it will be empty so nothing will be added.
      Authorization: `Bearer ${localStorage.getItem(LOCAL_STORAGE_KEYS.token)}`,
      ...Request.headers,
    };

    if (settings.hasContentType) {
      Request.headers = {
        ...Request.headers,
        "Content-type": "application/json; charset=UTF-8",
      };
    }

    const fetchURL = applySlashURL(baseURL, url);
    const res = await fetch(fetchURL, Request);

    if (settings.shouldHandleOutdated) await this.handleOutdated(res);

    if (settings.decrypt && previewAble.includes(res.status)) {
      const payload = await this.decryption(res);

      const newResponse = new Response(JSON.stringify(payload), {
        status: res.status,
        statusText: res.statusText,
        headers: res.headers,
      });

      if (settings.shouldHandleError && res.ok === false) {
        throw newResponse;
      } else {
        return newResponse;
      }
    } else {
      if (settings.shouldHandleError && res.ok === false) {
        throw res;
      } else {
        return res;
      }
    }
  };
}

// A manager for before sending the request.
abstract class FetchInterceptor {
  private static parseURLwithEventId = (pathname: string, eventId: string) => {
    if (pathname.includes("?")) {
      pathname = `${pathname}&event_id=${eventId}`;
    } else {
      pathname = `${pathname}?event_id=${eventId}`;
    }

    return pathname;
  };

  private static parseRequestWithEventId = (
    request: RequestInit | null,
    eventId: string,
  ) => {
    let newBody = request?.body;
    if (newBody instanceof FormData) {
      newBody.append("event_id", eventId);
    } else if (typeof newBody === "string") {
      const tmp = safeParse(newBody);
      tmp["event_id"] = eventId;
      newBody = JSON.stringify(tmp);
    }

    return {
      ...request,
      body: newBody as BodyInit,
    };
  };

  private static encryption = async (request: RequestInit) => {
    const body = safeParse(request.body);
    const jwe = await JweManager.encrypt(body);

    return {
      ...request,
      body: JSON.stringify({ payload: jwe }),
    };
  };

  public static fetchInterceptors = async (
    url: string,
    request: RequestInit | null,
    fetchOpts: FetchOpts,
  ): Promise<[string, RequestInit | null, FetchOpts]> => {
    const settings: FetchInterceptionOptions = {
      slugTarget: null,
      encrypt: false,
    };

    if (fetchOpts) Object.assign(settings, fetchOpts);

    const isLogin =
      url.includes("login") ||
      url.includes("session") ||
      url.includes("magic-link");
    if (settings.slugTarget && !isLogin) {
      if (settings.eventId) {
        if (settings.slugTarget === "url") {
          url = this.parseURLwithEventId(url, settings.eventId);
        }

        if (settings.slugTarget === "body") {
          request = this.parseRequestWithEventId(request, settings.eventId);
        }
      }
    }

    if (
      settings.encrypt &&
      request &&
      request.hasOwnProperty("body") &&
      request.body
    ) {
      request = await this.encryption(request);
    }

    return [url, request, fetchOpts];
  };
}

const fetchBase = async (
  url: string,
  request: RequestInit | null,
  fetchOpts: FetchOpts,
): Promise<Response> => {
  // prettier-ignore
  const [URL, REQUEST, FETCHOPTS] = await FetchInterceptor
    .fetchInterceptors(url, request, fetchOpts);

  // prettier-ignore
  return FetchBase
    .fetchBase(URL, REQUEST, FETCHOPTS);
};

export default fetchBase;
